import { getDataApiUrl, API_CONFIG } from '../config/api';
import authService from './authService';

export interface CalibrationPoint {
  rawData: number;
  liters: number;
}

export interface CalibrationTable {
  id: number;
  name: string;
  carId: number;
  carName: string;
  sensorNumber: number;
  sensorType: string;
  calibrationData: CalibrationPoint[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCalibrationTableRequest {
  name: string;
  carId: number;
  sensorNumber: number;
  sensorType: string;
  calibrationData: CalibrationPoint[];
}

export interface UpdateCalibrationTableRequest {
  name?: string;
  sensorType?: string;
  calibrationData?: CalibrationPoint[];
}

export interface CalibrationTableSearchRequest {
  name?: string;
  carId?: number;
  sensorNumber?: number;
  sensorType?: string;
  isActive?: boolean;
  createdAtFrom?: string;
  createdAtTo?: string;
}

export interface ValidationResult {
  isValid: boolean;
  message: string;
  validationDetails: {
    totalPoints?: number;
    minRawData?: number;
    maxRawData?: number;
    minLiters?: number;
    maxLiters?: number;
    isMonotonic?: boolean;
    hasZeroPoint?: boolean;
    errors?: string[];
  };
}

export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  numberOfElements: number;
}

class CalibrationService {
  private baseUrl = '/api/calibration-tables';

  private getAuthHeaders(): HeadersInit {
    const token = authService.getAccessToken();
    return {
      ...API_CONFIG.HEADERS,
      'Authorization': `Bearer ${token}`
    };
  }

  // Получение всех тарировочных таблиц с пагинацией
  async getCalibrationTables(
    page: number = 0,
    size: number = 20,
    sortBy: string = 'id',
    sortDir: 'ASC' | 'DESC' = 'ASC'
  ): Promise<PaginatedResponse<CalibrationTable>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      sortBy,
      sortDir
    });

    const url = getDataApiUrl(`${this.baseUrl}?${params}`);
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Получение активных тарировочных таблиц
  async getActiveCalibrationTables(): Promise<CalibrationTable[]> {
    const url = getDataApiUrl(`${this.baseUrl}/active`);
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Получение тарировочной таблицы по ID
  async getCalibrationTableById(id: number): Promise<CalibrationTable> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
      const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Получение тарировочных таблиц по машине
  async getCalibrationTablesByCar(carId: number): Promise<CalibrationTable[]> {
    const url = getDataApiUrl(`${this.baseUrl}/car/${carId}`);
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

    return response.json();
  }

  // Получение тарировочной таблицы по машине и номеру датчика
  async getCalibrationTableBySensor(carId: number, sensorNumber: number): Promise<CalibrationTable> {
    const url = getDataApiUrl(`${this.baseUrl}/car/${carId}/sensor/${sensorNumber}`);
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Создание новой тарировочной таблицы
  async createCalibrationTable(data: CreateCalibrationTableRequest): Promise<CalibrationTable> {
    const url = getDataApiUrl(this.baseUrl);
    const response = await fetch(url, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Обновление тарировочной таблицы
  async updateCalibrationTable(id: number, data: UpdateCalibrationTableRequest): Promise<CalibrationTable> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
      const response = await fetch(url, {
        method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Удаление тарировочной таблицы
  async deleteCalibrationTable(id: number): Promise<void> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
    const response = await fetch(url, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
  }

  // Поиск тарировочных таблиц
  async searchCalibrationTables(
    searchData: CalibrationTableSearchRequest,
    page: number = 0,
    size: number = 20
  ): Promise<PaginatedResponse<CalibrationTable>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString()
    });

    const url = getDataApiUrl(`${this.baseUrl}/search?${params}`);
    const response = await fetch(url, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(searchData)
    });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

    return response.json();
  }

  // Активация/деактивация тарировочной таблицы
  async toggleCalibrationTable(id: number): Promise<CalibrationTable> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}/toggle`);
    const response = await fetch(url, {
      method: 'PATCH',
      headers: this.getAuthHeaders()
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Валидация калибровочных данных
  async validateCalibrationData(calibrationData: CalibrationPoint[]): Promise<ValidationResult> {
    const url = getDataApiUrl(`${this.baseUrl}/validate`);
    const response = await fetch(url, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ calibrationData })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Экспорт тарировочной таблицы в CSV
  async exportCalibrationTableToCsv(id: number): Promise<Blob> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}/export/csv`);
      const response = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

    return response.blob();
  }

  // Импорт тарировочной таблицы из CSV
  async importCalibrationTableFromCsv(file: File): Promise<CalibrationTable> {
    const url = getDataApiUrl(`${this.baseUrl}/import/csv`);
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authService.getAccessToken()}`
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Получение стандартных типов датчиков
  getSensorTypes(): string[] {
    return [
      'FLS485',
      'ULTRASONIC',
      'CAPACITIVE',
      'MAGNETIC',
      'PRESSURE',
      'CUSTOM'
    ];
  }

  // Создание стандартной тарировочной таблицы для демонстрации
  createDefaultCalibrationTable(carId: number, sensorNumber: number): CreateCalibrationTableRequest {
    return {
      name: `Тарировка датчика №${sensorNumber}`,
      carId,
      sensorNumber,
      sensorType: 'FLS485',
      calibrationData: [
        { rawData: 0.0, liters: 0.0 },
        { rawData: 100.0, liters: 50.0 },
        { rawData: 200.0, liters: 100.0 },
        { rawData: 300.0, liters: 150.0 },
        { rawData: 400.0, liters: 200.0 }
      ]
    };
  }

  // Валидация калибровочных данных на клиенте
  validateCalibrationDataLocally(calibrationData: CalibrationPoint[]): ValidationResult {
    const errors: string[] = [];

    // Проверка минимального количества точек
    if (calibrationData.length < 2) {
      errors.push('Калибровочные данные должны содержать минимум 2 точки');
    }

    // Проверка наличия точки с rawData = 0.0
    const hasZeroPoint = calibrationData.some(point => point.rawData === 0.0);
    if (!hasZeroPoint) {
      errors.push('Должна быть точка с rawData = 0.0');
    }

    // Проверка монотонности rawData
    const sortedData = [...calibrationData].sort((a, b) => a.rawData - b.rawData);
    const isMonotonic = sortedData.every((point, index) => {
      if (index === 0) return true;
      return point.rawData > sortedData[index - 1].rawData;
    });

    if (!isMonotonic) {
      errors.push('Значения rawData должны быть монотонно возрастающими');
    }

    // Проверка соответствия литров
    const isLitersValid = sortedData.every((point, index) => {
      if (index === 0) return true;
      return point.liters >= sortedData[index - 1].liters;
    });

    if (!isLitersValid) {
      errors.push('Значения liters должны соответствовать возрастающим rawData');
    }

    const isValid = errors.length === 0;

    return {
      isValid,
      message: isValid ? 'Калибровочные данные корректны' : 'Калибровочные данные некорректны',
      validationDetails: {
        totalPoints: calibrationData.length,
        minRawData: Math.min(...calibrationData.map(p => p.rawData)),
        maxRawData: Math.max(...calibrationData.map(p => p.rawData)),
        minLiters: Math.min(...calibrationData.map(p => p.liters)),
        maxLiters: Math.max(...calibrationData.map(p => p.liters)),
        isMonotonic,
        hasZeroPoint,
        errors: errors.length > 0 ? errors : undefined
      }
    };
  }

  // Применение тарировки к сырому значению
  applyCalibration(rawData: number, calibrationData: CalibrationPoint[]): number {
    if (!calibrationData || calibrationData.length < 2) {
      return rawData; // Возвращаем исходное значение, если нет калибровки
    }

    // Сортируем точки по возрастанию rawData
    const sortedData = [...calibrationData].sort((a, b) => a.rawData - b.rawData);

    // Если значение меньше минимального
    if (rawData <= sortedData[0].rawData) {
      return sortedData[0].liters;
    }

    // Если значение больше максимального
    if (rawData >= sortedData[sortedData.length - 1].rawData) {
      return sortedData[sortedData.length - 1].liters;
    }

    // Находим ближайшие точки для интерполяции
    let lowerIndex = 0;
    for (let i = 0; i < sortedData.length - 1; i++) {
      if (rawData >= sortedData[i].rawData && rawData <= sortedData[i + 1].rawData) {
        lowerIndex = i;
        break;
      }
    }

    const lowerPoint = sortedData[lowerIndex];
    const upperPoint = sortedData[lowerIndex + 1];

    // Линейная интерполяция
    const ratio = (rawData - lowerPoint.rawData) / (upperPoint.rawData - lowerPoint.rawData);
    const liters = lowerPoint.liters + ratio * (upperPoint.liters - lowerPoint.liters);

    return Math.round(liters * 100) / 100; // Округляем до 2 знаков после запятой
  }
}

const calibrationService = new CalibrationService();
export default calibrationService; 